import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  HStack,
  IconButton,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  Divider,
  Spacer,
} from "@chakra-ui/react";
import { FiPlus, FiTrash } from "react-icons/fi";

/**
 * Manual-data entry component.
 *
 *  ▸ coldOutreach  – classic grid + Save
 *  ▸ cross_sell     – step-1 grid ➜ Next ➜ step-2 questions (+ Back) ➜ Save
 */
const ManualDataTab = ({
  /* parent-supplied props */
  columns: coldOutreachColumns,
  manualData,
  selectedRows,
  selectAll,
  countryOptions,
  isSaveLoading,
  contentType,
  campaignType,
  handleSelectAll,
  handleSelectRow,
  handleRemoveSelectedRows,
  handleAddRow,
  handleInputChange,
  handleSaveManualData,
  getColumnHeader,
}) => {
  /* step control (only for cross-sell) */
  const [crossSellStep, setCrossSellStep] = useState(1);
  useEffect(() => setCrossSellStep(1), [campaignType]);

  /* columns for step-1 */
  // const columns = useMemo(() => {
  //   if (campaignType !== "cross_sell") {
  //     return coldOutreachColumns;
  //   }
  //   // cross-sell hides the personalised_source column
  //   return coldOutreachColumns.filter((c) => c !== "personalised_source");
  // }, [campaignType, coldOutreachColumns]);
  const columns = useMemo(() => coldOutreachColumns, [coldOutreachColumns]);

  const isCountryField = (col) =>
    col === "searching_news_in_country" || col === "target_country";

  /* step-2 question list */
  const questions = [
    {
      key: "current_client_main_contact_full_name",
      prompt: "Current client – main contact’s full name:",
    },
    {
      key: "current_client_main_contact_job_title",
      prompt: "Current client – main contact’s job title:",
    },
    {
      key: "current_client_main_contact_department",
      prompt: "Current client – main contact’s department:",
    },
    {
      key: "current_client_purchased_proposition",
      prompt: "What proposition does the current client already buy from us?",
    },
    {
      key: "current_client_company_name",
      prompt: "Current client company name:",
    },
    {
      key: "current_client_relationship_to_target",
      prompt:
        "Relationship to target company (Same Company / Parent / Sister):",
    },
    {
      key: "current_client_additional_details",
      prompt: "Any specifics to add to the generated content:",
    },
  ];

  // Propagate one answer across all rows
  const handleQuestionChange = (key, value) => {
    manualData.forEach((_, rowIndex) => {
      handleInputChange(rowIndex, key, value);
    });
  };

  return (
    <>
      {/* STEP-1 table (or cold-outreach) */}
      {(campaignType !== "cross_sell" || crossSellStep === 1) && (
        <VStack spacing={4} align="stretch" h="45vh" overflowY="auto">
          <HStack justifyContent="space-between">
            <IconButton
              onClick={handleRemoveSelectedRows}
              icon={<FiTrash />}
              colorScheme="red"
              size="sm"
              isDisabled={selectedRows.length === 0}
              aria-label="Remove Selected Rows"
            />
            <IconButton
              onClick={handleAddRow}
              icon={<FiPlus />}
              colorScheme="green"
              size="sm"
              aria-label="Add Row"
            />
          </HStack>

          <Box>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th pr={2} display="flex" alignItems="center">
                    <Checkbox
                      isChecked={selectAll}
                      onChange={handleSelectAll}
                    />
                    <Text ml={2} mb={0}>
                      {selectedRows.length > 0 ? "Select All" : "Select"}
                    </Text>
                  </Th>
                  {columns.map((col) => (
                    <Th key={col}>{getColumnHeader(col)}</Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {manualData.map((row, rowIndex) => (
                  <Tr key={rowIndex}>
                    <Td>
                      <Checkbox
                        isChecked={selectedRows.includes(rowIndex)}
                        onChange={() => handleSelectRow(rowIndex)}
                      />
                    </Td>
                    {columns.map((col) => (
                      <Td key={col} verticalAlign="middle">
                        {campaignType === "cross_sell" &&
                        col === "prospect_company_name" ? (
                          <Input
                            value={manualData[0]?.[col] || ""}
                            onChange={(e) =>
                              handleQuestionChange(col, e.target.value)
                            }
                             placeholder="Enter target_company_name"
                            size="xs"
                            borderRadius="xl"
                          />
                        ) : isCountryField(col) ? (
                          <Select
                            value={row[col] || ""}
                            onChange={(e) =>
                              handleInputChange(rowIndex, col, e.target.value)
                            }
                            placeholder="Select country"
                            size="xs"
                            borderRadius="xl"
                          >
                            {countryOptions.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </Select>
                        ) : col === "personalised_source" ? (
                          <Select
                            value={row[col] || "LinkedIn About Section"}
                            onChange={(e) =>
                              handleInputChange(rowIndex, col, e.target.value)
                            }
                            size="xs"
                            borderRadius="xl"
                            textAlign="left"
                          >
                            <option value="LinkedIn About Section">
                              LinkedIn About Section (Default)
                            </option>
                            <option value="AI Created Persona">
                              AI Created Persona
                            </option>
                          </Select>
                        ) : col === "prospect_linkedin_about_section" ? (
                          <Input
                            value={row[col] || ""}
                            onChange={(e) =>
                              handleInputChange(rowIndex, col, e.target.value)
                            }
                            placeholder="Enter LinkedIn about section"
                            size="xs"
                            borderRadius="xl"
                            isDisabled={
                              row["personalised_source"] !==
                              "LinkedIn About Section"
                            }
                          />
                        ) : (
                          <Input
                            value={row[col] || ""}
                            onChange={(e) =>
                              handleInputChange(rowIndex, col, e.target.value)
                            }
                            placeholder={`Enter ${getColumnHeader(col)}`}
                            size="xs"
                            borderRadius="xl"
                          />
                        )}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </VStack>
      )}

      {/* STEP-2 questions (cross-sell only) */}
      {campaignType === "cross_sell" && crossSellStep === 2 && (
        <Box p={4} bg="gray.50" borderRadius="md">
          <Text fontWeight="bold" mb={4}>
            Questions
          </Text>
          {questions.map(({ key, prompt }) => (
            <Box mb={3} key={key}>
              <Text mb={1} fontSize="sm">
                {prompt}
              </Text>
              {key === "current_client_relationship_to_target" ? (
                <Select
                  size="sm"
                  borderRadius="xl"
                  placeholder="Select relationship"
                  value={manualData[0]?.[key] || ""}
                  onChange={(e) => handleQuestionChange(key, e.target.value)}
                >
                  <option value="Same Company">Same Company</option>
                  <option value="Parent Company">Parent Company</option>
                  <option value="Sister Company">Sister Company</option>
                </Select>
              ) : (
                <Input
                  size="sm"
                  borderRadius="xl"
                  value={manualData[0]?.[key] || ""}
                  onChange={(e) => handleQuestionChange(key, e.target.value)}
                  placeholder="Enter answer"
                />
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* FOOTER */}
      <Box mt={4} display="flex" justifyContent="space-between">
        {campaignType === "cross_sell" && crossSellStep === 2 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCrossSellStep(1)}
          >
            Back
          </Button>
        )}
        <Spacer />
        {campaignType === "cross_sell" && crossSellStep === 1 ? (
          <Button
            bg="#231F1F"
            color="#FFFFFF"
            _hover={{ bg: "#404040" }}
            borderRadius="full"
            size="sm"
            onClick={() => setCrossSellStep(2)}
            isDisabled={manualData.length === 0}
          >
            Next
          </Button>
        ) : (
          <Button
            bg="#231F1F"
            color="#FFFFFF"
            _hover={{ bg: "#404040" }}
            borderRadius="full"
            size="sm"
            onClick={handleSaveManualData}
            isLoading={isSaveLoading}
          >
            Save
          </Button>
        )}
      </Box>
      {/* personalised guidance */}
      {contentType === "personalised" && campaignType !== "cross_sell" && (
        <>
          <Divider />
          <VStack align="start" spacing={1}>
            <Text fontSize="sm" color="gray.600" mb={0}>
              • Copy the text in the “About” section from the prospect’s
              LinkedIn profile and paste it above.
            </Text>
            <Text fontSize="sm" color="gray.600" mb={0}>
              • If no text exists, switch the dropdown to “AI Created Persona”
              to generate generic content.
            </Text>
          </VStack>
        </>
      )}
    </>
  );
};

export default ManualDataTab;
